import React from "react";
import { connect } from "react-redux";

import { permDeleteFiles } from "../../state/files/Folders";
import { initialiseImports } from "../../state/files/Imports";
import store, { IAppState } from "../../state/Store";
import { addAlert } from "../../state/system/Alerts";
import File from "../../types/files/File";
import Upload from "../../types/files/Upload";
import AlertEntry, { AlertTypes } from "../../types/system/AlertEntry";
import { Notification } from "../../types/system/Notification";
import MessageDetail from "../../types/system/MessageDetail";
import UserDigest from "../../types/core/UserDigest";
import {
    clearNewUnread,
    initialiseMessaging,
    openMessage,
} from "../../state/system/Messaging";
import { setMessagingOpen } from "../../state/system/UIConfiguration";

import userIcon from "../../graphics/profile.svg";

import "./NotificationsMenu.scss";

interface NotificationsMenuProps {
    open: boolean;
    notifications: Notification[];
    messages: MessageDetail[]; // Placeholder type for messages, replace with actual type when available
    users: UserDigest[];
    imports: File[];
    uploads: Upload[];
    initialiseImports: Function;
    initialiseMessaging: Function;
    openMessage: (message: MessageDetail) => void;
    addAlert: (alert: AlertEntry) => void;
    deleteFiles: (files: File[]) => Promise<void>;
    clearNewUnread: () => void;
    setMessagingOpen: (open: boolean) => void;
    notificationsShouldOpen?: () => void;
    notificationsShoulClose?: () => void;
}

interface NotificationsMenuState {
    removedNotifications: Notification[];
    removedMessages: MessageDetail[];
    canceledImports: File[];
    canceledUploads: Upload[];
    displayNotifications: MessageDetail[];
}

class NotificationsMenu extends React.Component<
    NotificationsMenuProps,
    NotificationsMenuState
> {
    state = {
        removedNotifications: [],
        removedMessages: [],
        canceledImports: [],
        canceledUploads: [],
        displayNotifications: [],
    };

    componentDidMount(): void {
        this.props.initialiseImports();
        this.props.initialiseMessaging();
        store.subscribe(this.forceUpdate.bind(this));
    }

    componentDidUpdate(
        prevProps: Readonly<NotificationsMenuProps>,
        prevState: Readonly<{}>,
        snapshot?: any,
    ): void {
        if (
            (prevProps.imports.length === 0 && this.props.imports.length > 0) ||
            (prevProps.uploads.length === 0 && this.props.uploads.length > 0)
        ) {
            this.props.notificationsShouldOpen?.();
        }
        if (
            (prevProps.imports.length > 0 &&
                this.props.imports.length === 0 &&
                this.props.uploads.length === 0) ||
            (prevProps.uploads.length > 0 &&
                this.props.uploads.length === 0 &&
                this.props.imports.length === 0)
        ) {
            this.props.notificationsShoulClose?.();
        }

        if (this.props.open && !prevProps.open) this.props.clearNewUnread();
    }

    notificationClick(item: Notification) {
        // Placeholder for handling notification click events
        console.log("Notification clicked:", item);
    }

    removeNotification(item: Notification) {
        this.setState(prevState => ({
            removedNotifications: [...prevState.removedNotifications, item],
        }));
    }

    addMessage() {
        this.props.setMessagingOpen(true);
    }

    removeMessage(item: MessageDetail) {
        this.setState(prevState => ({
            removedMessages: [...prevState.removedMessages, item],
        }));
    }

    userClick(item: UserDigest) {
        // Placeholder for handling user click events (e.g., open messaging with user)
        console.log("User clicked:", item);
    }

    displayNotificationsProcessed() {
        this.setState({ displayNotifications: [] });
    }

    removeUpload(_, target: string, value: string) {
        const uploadItem = this.props.uploads.find(
            item => item.id.toString() === value,
        );

        if (uploadItem && !this.state.canceledImports.includes(uploadItem)) {
            this.props.addAlert({
                type: AlertTypes.Confirm,
                title: "Confirm Cancel",
                text: `Are you sure you want to cancel the upload of ${uploadItem.name}?`,
                callback: async (_: AlertEntry) => {
                    this.setState(() => ({
                        canceledUploads: [uploadItem].concat(
                            this.state.canceledUploads,
                        ),
                    }));
                    uploadItem.abortUpload();
                    this.setState(() => ({ canceledImports: [] }));
                },
            } as AlertEntry);
        }
    }

    resolveImport(value: string) {
        const parts = value.split(":");
        const type = parts[0];
        const id = Number(parts[1]);

        return this.props.imports.find(
            item => item.fileType.toString() === type && item.id === id,
        );
    }
    removeImport(_, target: string, value: string) {
        const importItem = this.resolveImport(value);

        if (importItem && !this.state.canceledImports.includes(importItem)) {
            this.props.addAlert({
                type: AlertTypes.Confirm,
                title: "Confirm Delete",
                text: `Are you sure you want to cancel the import of ${importItem.name}?`,
                callback: async (_: AlertEntry) => {
                    this.setState(() => ({
                        canceledImports: [importItem].concat(
                            this.state.canceledImports,
                        ),
                    }));
                    await this.props.deleteFiles([importItem]);
                    this.setState(() => ({ canceledImports: [] }));
                },
            } as AlertEntry);
        }
    }

    render() {
        return (
            <>
                {this.props.open && (
                    <div className="apps-menu-slide notifications">
                        <div className="sidebar-menu apps-menu">
                            {/* Notifications Section */}
                            <div className="notification-links app-items buttons">
                                <span className="title">Notifications</span>

                                <div className="items">
                                    {/* Individual notification items - limited to 3 most recent */}
                                    {this.props.notifications
                                        .filter(
                                            notification =>
                                                !this.state.removedNotifications.includes(
                                                    notification,
                                                ),
                                        )
                                        .sort(
                                            (a, b) =>
                                                b.date.getTime() -
                                                a.date.getTime(),
                                        )
                                        .slice(0, 3)
                                        .map((item, index) => (
                                            <button
                                                key={index}
                                                className={`item mock-link${
                                                    item.emphasize
                                                        ? " emphasize"
                                                        : ""
                                                }`}
                                                onClick={() =>
                                                    this.notificationClick(item)
                                                }>
                                                {/* Remove button for dismissing notification */}
                                                <span
                                                    className="remove"
                                                    onClick={() =>
                                                        this.removeNotification(
                                                            item,
                                                        )
                                                    }
                                                    data-tip="Clear this notification"
                                                    data-place="left"></span>
                                                <span>{"{{item.text}}"}</span>
                                            </button>
                                        ))}

                                    {/* Footer showing count of additional notifications */}
                                    {this.props.notifications.length > 3 && (
                                        <span className="footer">
                                            {`${
                                                this.props.notifications
                                                    .length - 3
                                            } more not shown`}
                                        </span>
                                    )}

                                    {/* Empty state message */}
                                    {this.props.notifications.length === 0 && (
                                        <span>No notifications</span>
                                    )}
                                </div>
                            </div>

                            {/* Messaging Section */}
                            <div className="notification-links app-items buttons">
                                <span className="title">Messaging</span>

                                <div className="items">
                                    {/* Button to open messaging dashboard */}
                                    <button
                                        className="item button"
                                        onClick={() => this.addMessage()}>
                                        <div className="icon-cont">
                                            <i>chat_bubble</i>
                                        </div>
                                        <span>Messaging Dashboard</span>
                                    </button>

                                    <hr />

                                    {/* Recent messages list - limited to 3 most recent */}
                                    {this.props.messages
                                        .filter(
                                            message =>
                                                !this.state.removedMessages.includes(
                                                    message,
                                                ),
                                        )
                                        .sort(
                                            (a, b) =>
                                                new Date(
                                                    b.CreatedOn || 0,
                                                ).getTime() -
                                                new Date(
                                                    a.CreatedOn || 0,
                                                ).getTime(),
                                        )
                                        .slice(0, 3)
                                        .map((item, index) => (
                                            <button
                                                key={index}
                                                className="item message"
                                                onClick={() =>
                                                    this.props.openMessage(item)
                                                }>
                                                {/* Remove button for dismissing message */}
                                                <span
                                                    className="remove"
                                                    onClick={() =>
                                                        this.removeMessage(item)
                                                    }
                                                    data-tip="Clear this message"
                                                    data-place="left"></span>

                                                {/* Sender name with tooltip */}
                                                <span
                                                    className="sender"
                                                    data-tip={
                                                        item.SenderDetails
                                                    }
                                                    data-place="left">
                                                    {item.SenderDetails}
                                                </span>

                                                {/* Message preview text */}
                                                <span className="message">
                                                    {item.BareMessage}
                                                </span>
                                            </button>
                                        ))}

                                    {/* Footer showing count of additional messages */}
                                    {this.props.messages.length > 3 && (
                                        <span className="footer">
                                            {`${
                                                this.props.messages.length - 3
                                            } more not shown`}
                                        </span>
                                    )}

                                    {/* Empty state message */}
                                    {this.props.messages.length === 0 && (
                                        <span>No messages</span>
                                    )}
                                </div>
                            </div>

                            {/* Other Users Section */}
                            <div className="config-links app-items buttons">
                                {this.props.users.length !== 0 && (
                                    <>
                                        <span className="title">
                                            Other Users
                                        </span>

                                        <div className="items users">
                                            {/* List of users available for messaging */}
                                            {this.props.users.map(
                                                (item, index) => (
                                                    <button
                                                        key={index}
                                                        className="item"
                                                        data-tip={`User: ${item.username} - ${item.firstName}  ${item.lastName}`}
                                                        data-place="left"
                                                        onClick={() =>
                                                            this.userClick(item)
                                                        }>
                                                        <div className="icon-cont">
                                                            {/* Default profile picture */}
                                                            {!!item.profilePictureFile ? (
                                                                <img
                                                                    src={
                                                                        item.profilePictureFile
                                                                    }
                                                                    alt="User Icon"></img>
                                                            ) : (
                                                                <img
                                                                    src={
                                                                        userIcon
                                                                    }
                                                                    alt="User Icon"></img>
                                                            )}
                                                        </div>

                                                        <span>
                                                            {item.firstName}{" "}
                                                            {item.lastName}
                                                        </span>
                                                    </button>
                                                ),
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { imports, uploads, messaging } = state.db;
    return {
        messages: messaging.unread,
        imports: imports.imports,
        uploads: uploads.uploads,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        initialiseImports: () => dispatch(initialiseImports()),
        initialiseMessaging: () => dispatch(initialiseMessaging()),
        openMessage: (message: MessageDetail) => dispatch(openMessage(message)),
        addAlert: (alert: AlertEntry) => dispatch(addAlert(alert)),
        deleteFiles: (files: File[]) => dispatch(permDeleteFiles(files)),
        clearNewUnread: () => dispatch(clearNewUnread()),
        setMessagingOpen: (open: boolean) => dispatch(setMessagingOpen(open)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(NotificationsMenu);
